import { Select, ToggleField, toOptions } from '~/components/form';
import { FilterDrawer } from '~/components/ui';
import { MLS_DATA_TYPES, MLS_VENDORS } from '~/components/Constants/mlsAgreementsConstants';

type Filters = Record<string, string>;

/**
 * URL keys of each list's filters, `deleted` included.
 * No status filter: MLS and customer status is free text matched exactly, so a fixed list would miss.
 */
export const DELETED_KEY = 'deleted';
export const MLS_FILTER_KEYS = ['vendor', 'dataType', DELETED_KEY] as const;
export const CUSTOMER_FILTER_KEYS = [DELETED_KEY] as const;

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  value: Filters;
  onApply: (filters: Filters) => void;
  onClear: () => void;
}

const DeletedToggle = ({
  id,
  text,
  draft,
  patch,
}: {
  id: string;
  text: string;
  draft: Filters;
  patch: (next: Filters) => void;
}) => (
  <ToggleField
    id={id}
    testId={id}
    label={text}
    text={`Show ${text.toLowerCase()}`}
    checked={draft[DELETED_KEY] === 'true'}
    onChange={(on) => patch({ [DELETED_KEY]: on ? 'true' : '' })}
  />
);

export function MlsFilterDrawer(drawer: DrawerProps) {
  return (
    <FilterDrawer<Filters> {...drawer} testId="mls-agreements-mls-filters">
      {(draft, patch) => (
        <>
          <Select
            id="mls-filter-vendor"
            label="Vendor"
            value={draft.vendor ?? ''}
            options={toOptions(MLS_VENDORS)}
            placeholder="All vendors"
            placeholderSelectable
            onChange={(vendor) => patch({ vendor })}
          />
          <Select
            id="mls-filter-data-type"
            label="Data type"
            value={draft.dataType ?? ''}
            options={toOptions(MLS_DATA_TYPES)}
            placeholder="All data types"
            placeholderSelectable
            onChange={(dataType) => patch({ dataType })}
          />
          <DeletedToggle
            id="mls-filter-show-deleted"
            text="Deleted MLSs"
            draft={draft}
            patch={patch}
          />
        </>
      )}
    </FilterDrawer>
  );
}

/** Its one filter is "Show deleted". */
export function CustomerFilterDrawer(drawer: DrawerProps) {
  return (
    <FilterDrawer<Filters> {...drawer} testId="mls-agreements-customers-filters">
      {(draft, patch) => (
        <DeletedToggle
          id="customer-filter-show-deleted"
          text="Deleted customers"
          draft={draft}
          patch={patch}
        />
      )}
    </FilterDrawer>
  );
}
